import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Grid2,
} from '@mui/material';
import { useAppSelector } from '../../redux/hook.ts';
import ScoringService from '../../api_handlers/services/ScoringService.ts';
import { useToast } from '../base_components/ToastContext.tsx';
import VotingService from '../../api_handlers/services/VotingService.ts';

interface Collectible {
  collectibleId: number;
  collectibleName: string;
  collectibleCount: number;
  multiplier: number;
  collectibleTotal: number;
}

interface Category {
  catagoryName: string;
  rows: Collectible[];
}

interface ScoreCard {
  team: { id: number; name: string };
  kopels: Category;
  preciousStones: Category;
  standardCredit: number;
  shumasPercentage: number; // Add shumasPercentage to the ScoreCard interface
}

interface TeamDetailsDialogProps {
  team: any;
  onClose: () => void;
}

const TeamDetailsDialog = ({ team, onClose }: TeamDetailsDialogProps) => {
  const session = useAppSelector((state) => state?.fSession);
  const sessionId = session?.session?.sessionId;
  const fEmail = session?.user?.email;
  const { showToast } = useToast();

  const [scoreCard, setScoreCard] = useState<ScoreCard | null>(null);
  const [changedCollectibles, setChangedCollectibles] = useState<{ [key: number]: number }>({});
  const [percentage, setPercentage] = useState<number | undefined>(undefined); // State for shumas percentage
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const { data } = await ScoringService.getScoreCard({ sessionId, teamId: team.teamId });
        setScoreCard(data.scoreCard);
        setPercentage(team.percentage); // Set initial percentage
      } catch (err) {
        console.error('Error fetching scorecard:', err);
      }
    };

    fetchTeam();
  }, [sessionId, team]);

  const handleCollectibleCountChange = (collectibleId: number, newCount: number) => {
    setChangedCollectibles((prevChangedCollectibles) => ({
      ...prevChangedCollectibles,
      [collectibleId]: newCount,
    }));
  };

  const handlePercentageChange = (newPercentage: number) => {
    setPercentage(newPercentage);
  };

  const saveChanges = async () => {
    if (!scoreCard) return;

    setLoading(true);

    const changedCollectiblesArray = Object.keys(changedCollectibles).map(Number);

    const collectiblesToUpdate: any = [];

    const compareAndCollectChanges = (category: Category) => {
      category.rows.forEach((row) => {
        if (
          changedCollectiblesArray.includes(row.collectibleId) &&
          changedCollectibles[row.collectibleId] !== row.collectibleCount
        ) {
          collectiblesToUpdate.push({
            collectibleTypeEnumId: row.collectibleId,
            sessionId,
            teamId: team.teamId,
            userId: -1,
            quantity: changedCollectibles[row.collectibleId],
            fEmail,
          });
        }
      });
    };

    compareAndCollectChanges(scoreCard.kopels);
    compareAndCollectChanges(scoreCard.preciousStones);

    // Include Standard Credit if it has changed
    if (
      changedCollectibles[7] !== undefined &&
      changedCollectibles[7] !== scoreCard.standardCredit
    ) {
      collectiblesToUpdate.push({
        collectibleTypeEnumId: 7,
        sessionId,
        teamId: team.teamId,
        userId: -1,
        quantity: changedCollectibles[7],
        fEmail,
      });
    }

    try {
      for (const collectible of collectiblesToUpdate) {
        await ScoringService.setCollectibleCount(collectible);
        showToast('Score updated successfully.');
      }

      // Update shumas percentage if it has changed
      if (percentage !== undefined && percentage !== scoreCard.shumasPercentage) {
        await VotingService.updatePercentages({ sessionId, teamId: team.teamId, percentage });
        showToast('Shumas percentage updated successfully.');
      }
    } catch (err) {
      showToast('Score update failed.');
      console.error('Error saving changes:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderCategory = (category: Category) => (
    <Box mb={3}>
      <Typography variant="h6" gutterBottom sx={{
        fontWeight: 'bold',
        fontFamily: "'Cinzel', serif",
        color: '#FFD700',
        textShadow: '0.1rem 0.1rem 0.2rem rgba(0,0,0,0.8)',
      }}>
        {category.catagoryName}
      </Typography>

      <Grid2 container spacing={2}>
        {category.rows.map((row) => {
          const count = changedCollectibles[row.collectibleId] ?? row.collectibleCount;
          return (
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={row.collectibleId}>
              <Box sx={{
                p: 2,
                border: '0.15rem solid rgba(218, 165, 32, 0.6)',
                borderRadius: '0.75rem',
                background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.3) 0%, rgba(101, 67, 33, 0.25) 100%)',
              }}>
                <div className="flex items-center gap-1 mb-1">
                  <img
                    src={`img/items-icons/${row.collectibleName.toLocaleLowerCase()}.png`}
                    alt={row.collectibleName}
                    className="w-10 h-10"
                  />
                  <Typography variant="body1" sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    fontFamily: "'Philosopher', sans-serif",
                    color: '#FFD700',
                  }}>
                    {row.collectibleName}
                  </Typography>
                </div>
                <TextField
                  type="number"
                  value={count === 0 ? '' : count}
                  size="small"
                  fullWidth
                  onChange={(e) =>
                    handleCollectibleCountChange(row.collectibleId, Number(e.target.value))
                  }
                  sx={{
                    mb: 1,
                    '& .MuiInputBase-root': {
                      color: '#FFD700',
                      background: 'rgba(255, 255, 255, 0.1)',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(218, 165, 32, 0.5)',
                    },
                  }}
                />
                <Typography variant="body2" sx={{
                  fontStyle: 'italic',
                  color: '#FFA500',
                  fontFamily: "'Philosopher', sans-serif",
                }}>
                  Total: {count * row.multiplier}
                </Typography>
              </Box>
            </Grid2>
          );
        })}
      </Grid2>
    </Box>
  );

  const renderStandardCredit = () => (
    <Box mb={3}>
      <div className="flex items-center gap-1 mb-1">
        <img
          src={`img/items-icons/standardCredits.png`}
          alt={'Standard Credit'}
          className="w-10 h-10"
        />
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Standard Credit
        </Typography>
      </div>
      <TextField
        type="number"
        value={changedCollectibles[7] === 0 ? '' : changedCollectibles[7] ?? scoreCard?.standardCredit ?? ''}
        size="small"
        onChange={(e) => handleCollectibleCountChange(7, Number(e.target.value))}
        sx={{ mb: 1, width: '40%' }}
      />
    </Box>
  );
  

  const renderShumasPercentage = () => (
    <Box mb={3}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        Shumas Percentage
      </Typography>
      <TextField
        type="number"
        value={percentage === 0 ? '' : percentage ?? ''}
        size="small"
        onChange={(e) => handlePercentageChange(Number(e.target.value))}
        sx={{ mb: 1, width: '40%' }}
      />
    </Box>
  );
  
  if (!scoreCard) return null;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth sx={{
      '& .MuiDialog-paper': {
        background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.98) 0%, rgba(101, 67, 33, 0.95) 100%)',
        backdropFilter: 'blur(10px)',
        border: '0.25rem solid rgba(218, 165, 32, 0.7)',
        borderRadius: '1rem',
        boxShadow: '0 1rem 3rem rgba(0, 0, 0, 0.9), 0 0 2rem rgba(218, 165, 32, 0.5)',
      }
    }}>
      <DialogTitle sx={{
        fontWeight: 'bold',
        fontSize: '1.5rem',
        fontFamily: "'Cinzel', serif",
        color: '#FFD700',
        textShadow: '0.15rem 0.15rem 0.3rem rgba(0,0,0,0.9), 0 0 0.8rem rgba(255, 215, 0, 0.6)',
      }}>
        {scoreCard.team.name}
      </DialogTitle>
      <DialogContent>
        {renderCategory(scoreCard.kopels)}
        {renderCategory(scoreCard.preciousStones)}
        {renderStandardCredit()}
        {renderShumasPercentage()}
      </DialogContent>
      <DialogActions sx={{ padding: '1rem 1.5rem' }}>
        <Button onClick={onClose} variant="contained" sx={{
          fontFamily: "'Cinzel', serif",
          fontWeight: 700,
          background: 'linear-gradient(135deg, #B22222 0%, #8B0000 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #DC143C 0%, #B22222 100%)',
          },
        }}>
          Close
        </Button>
        <Button
          onClick={saveChanges}
          variant="contained"
          disabled={loading}
          sx={{
            fontFamily: "'Cinzel', serif",
            fontWeight: 700,
            background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)',
            },
            '&:disabled': {
              background: 'rgba(128, 128, 128, 0.3)',
            }
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: '#FFD700' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamDetailsDialog;
